<template>
  <div
    class="notes-editor-container">
    <div class="heading-notes">
      Notes
    </div>
    <editor-floating-menu
      :editor="editor"
      ref="editorMenu"
      class="floating-menu-container"
      v-slot="{ commands, isActive, menu }">
      <div
        class="floating-menu"
        :class="{ 'is-active': menu.isActive && !menuHidden }"
        :style="`top: ${Math.min(
          !!$refs.editor && $refs.editor.$el.clientHeight + (1.8 * remInPx), /* to prevent the menu from overflowing the editor, which screws over the whole layout */
          menu.top
        )}px`">
        <div
          class="menu-item"
          :class="{ 'is-active': isActive.bold() }"
          @click="commands.bold">
          <i class="mdi mdi-format-bold mdi-18px" />
        </div>
        <div
          class="menu-item"
          :class="{ 'is-active': isActive.italic() }"
          @click="commands.italic">
          <i class="mdi mdi-format-italic mdi-18px" />
        </div>
        <div
          class="menu-item"
          :class="{ 'is-active': isActive.underline() }"
          @click="commands.underline">
          <i class="mdi mdi-format-underline mdi-18px" />
        </div>
        <div
          class="menu-item"
          :class="{ 'is-active': isActive.bullet_list() }"
          @click="commands.bullet_list">
          <i class="mdi mdi-format-list-bulleted mdi-18px" />
        </div>
        <div
          class="menu-item"
          :class="{ 'is-active': isActive.ordered_list() }"
          @click="commands.ordered_list">
          <i class="mdi mdi-format-list-numbered mdi-18px" />
        </div>
        <div
          class="menu-item"
          :class="{ 'is-active': isActive.heading({ level: 1 }) }"
          @click="commands.heading({ level: 1 })">
          <i class="mdi mdi-format-header-1 mdi-18px" />
        </div>
        <div
          class="menu-item"
          :class="{ 'is-active': isActive.heading({ level: 2 }) }"
          @click="commands.heading({ level: 2 })">
          <i class="mdi mdi-format-header-2 mdi-18px" />
        </div>
        <div
          class="menu-item"
          :class="{ 'is-active': isActive.heading({ level: 3 }) }"
          @click="commands.heading({ level: 3 })">
          <i class="mdi mdi-format-header-3 mdi-18px" />
        </div>
        <div
          class="menu-item"
          :class="{ 'is-active': isActive.blockquote() }"
          @click="commands.blockquote()">
          <i class="mdi mdi-format-quote-close mdi-18px" />
        </div>
        <div
          class="menu-item"
          :class="{ 'is-active': isActive.todo_list() }"
          @click="commands.todo_list()">
          <i class="mdi mdi-format-list-checks mdi-18px" />
        </div>
      </div>
    </editor-floating-menu>
    <editor-content
      v-scroll="storeScrollPosition"
      class="editor"
      :editor="editor"
      ref="editor"
      v-wheel="hideMenu" />
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { Editor, EditorContent, EditorFloatingMenu } from 'tiptap';
import { Bold, Italic, Underline, BulletList, OrderedList, ListItem, Heading, Blockquote, TodoItem, TodoList, History, Link } from 'tiptap-extensions';
const debounce = require('lodash/debounce');

export default {
  name: 'ItemViewNotesEditor',
  components: {
    EditorContent,
    EditorFloatingMenu,
  },
  directives: {
    wheel: {
      inserted: function (el, binding) { // eslint-disable-line 
        let f = function (evt) { // eslint-disable-line 
          if (binding.value(evt, el)) {
            el.removeEventListener('wheel', f);
          }
        };
        el.addEventListener('wheel', f);
      },
    },
    // Scroll is used for storing scroll position. I guess 'wheel' would also work for that though. Scroll now just for consistency.
    scroll: {
      inserted: function (el, binding) { // eslint-disable-line 
        let f = function (evt) { // eslint-disable-line 
          if (binding.value(evt, el)) {
            el.removeEventListener('scroll', f);
          }
        };
        el.addEventListener('scroll', f);
      },
    },
  },
  props: {
    currentCard: {
      type: Object,
      required: true,
      default: () => {},
    },
  },
  data() {
    return {
      editor: new Editor({
        extensions: [
          new Bold(),
          new Italic(),
          new Underline(),
          new BulletList(),
          new OrderedList(),
          new ListItem(),
          new Heading({ levels: [1, 2, 3] }),
          new Blockquote(),
          new TodoItem({ nested: true }),
          new TodoList(),
          new History(),
          new Link(),
        ],
        onUpdate: debounce(({ getHTML }) => {
          this.$updateCard({
            id: this.cardId,
            data: {
              note: getHTML(),
            },
          });
        }, 500),
        onTransaction: () => {
          this.menuHidden = false;
        },
        content: (this.currentCard && this.currentCard.note),
      }),
      menuHidden: false,
    };
  },
  computed: {
    cardId() {
      return Number(this.$route.params.cardId) || 0;
    },
    remInPx() {
      return parseFloat(getComputedStyle(document.documentElement).fontSize);
    },
  },
  methods: {
    ...mapActions({
      v_storeScrollPosition: 'appState/storeScrollPosition',
    }),
    hideMenu(event) {
      this.menuHidden = true;
    },
    storeScrollPosition: debounce(function(evt, el) { // eslint-disable-line
      // in CollectionView this.selectID is used to account for search and stuff. For notes atm not necessary.
      this.v_storeScrollPosition({ type: 'itemNotes', id: this.cardId, scrollPosition: el.scrollTop });
    }, 50),
  },
  watch: {
    currentCard(newCard, oldCard) {
      // It could be the case that the card is deleted from another client session. So if newCard is undefined, move away.
      if (!newCard) {
        this.$goToCollection({ id: oldCard.relatedCollectionId });
        return;
      }

      if (this.currentCard && this.currentCard.note) this.editor.setContent(this.currentCard.note);
      else this.editor.clearContent();

      const scrollToPosition = this.$store.state.appState.scrollState.itemNotes[this.cardId];
      if (scrollToPosition) this.$refs.editor.$el.scrollTo(0, scrollToPosition);
    },
    $route() {
      if (this.$route.name === 'WebViewer') this.hideMenu(); // Hide menu when going to itemView
    },
  },
  async mounted() {
    if (this.currentCard) {
      if (this.currentCard.note) this.editor.setContent(this.currentCard.note);
      else this.editor.clearContent();
    }

    const scrollToPosition = this.$store.state.appState.scrollState.itemNotes[this.noteId];
    await this.$nextTick(); // Else editor has not loaded yet and cannot scroll. Seems to delay loading the notes a bit, though. Ah well.
    if (scrollToPosition) this.$refs.editor.$el.scrollTo(0, scrollToPosition);
  },
  beforeDestroy() {
    this.editor.destroy();
  },
};
</script>

<style lang="scss" scoped>
@import '~styles/layouts';
@import '~styles/fonts';

.notes-editor-container {
  position: relative;
  height: calc(100vh - 4.25rem);
  max-height: calc(100vh - 4.25rem);
  overflow: hidden;
  background-color: #f8f9fa;
  margin: 0;
}
.heading-notes {
  position: relative;
  left: 1.5rem;
  top: 0.5rem;
  font-family: $headerFont;
  font-size: 1.5rem;
  height: 46px;
}
.floating-menu {
  position: absolute;
  z-index: 1;
  margin-top: -0.25rem;
  margin-left: 2.25rem;
  visibility: hidden;
  opacity: 0;
  transition: all 0.2s;
}
.is-active {
  opacity: 1 !important;
  visibility: visible;
}
.menu-item {
  display: inline-block;
  vertical-align: bottom;
  padding: 0 0.5vw;
  cursor: pointer;
  opacity: 0.6;
  transition: all 0.2s;
}
.menu-item:hover {
  opacity: 1;
}
.editor {
  @include prosemirror-for-editor;
  position: relative;
  left: 1rem;
  border-radius: 0.5rem 0 0 0;
  border: 1px solid #dbdbdb;
  width: calc(100% - 1rem);
  max-height: calc(100vh - 4.25rem - 47px);
  overflow-y: auto;
  overflow-x: hidden;
  background-color: #f5f5f5;
  transition: background-color 0.2s;

  ::v-deep .ProseMirror {
    min-height: calc(100vh - 4.25rem - 47px - 2px); // -2px to account for border of .editor
    // max-height: calc(100vh - 4.25rem - 47px);
    padding: 2rem 1.5rem 0rem;
    overflow-y: hidden;
    overflow-x: hidden;
  }
}
</style>
